import { Button } from "@/shared/components/Button";
import { Modal } from "@/shared/components/Modal";
import type { ComunicadoComRelacoes } from "@/modules/comunicados/services/comunicadosService";
import { ministeriosSoPara, rotuloDestino } from "@/modules/comunicados/utils/destino";
import { rotuloApagaEm, rotuloPublicado } from "@/modules/comunicados/utils/prazo";

interface ModalLeituraComunicadoProps {
  comunicado: ComunicadoComRelacoes | null;
  /** Ministérios de que a pessoa participa (para o aviso "só para …" da gestão). */
  meusMinisterios: ReadonlySet<string> | undefined;
  /** Só quem gerencia o elo edita; a RPC revalida de qualquer forma. */
  podeEditar: boolean;
  onEditar: (comunicado: ComunicadoComRelacoes) => void;
  onFechar: () => void;
}

/** Leitura completa de um comunicado, aberta na própria tela (sem rota nova). */
export function ModalLeituraComunicado({
  comunicado: c,
  meusMinisterios,
  podeEditar,
  onEditar,
  onFechar,
}: ModalLeituraComunicadoProps) {
  const soPara = c ? ministeriosSoPara(c, meusMinisterios) : null;

  return (
    <Modal titulo={c?.titulo ?? ""} aberto={!!c} onClose={onFechar}>
      {c && (
        <div className="flex flex-col gap-3">
          <p className="text-xs text-muted">
            Publicado: {rotuloPublicado(c.publicado_em).toLowerCase()}
            {c.editado_em ? " · editado" : ""}
            {c.detalhesVisiveis && (
              <>
                {" · "}
                {rotuloDestino(c).toLowerCase()}
                {" · "}
                {c.situacao === "arquivado"
                  ? "só apaga se alguém apagar"
                  : c.apagar_automaticamente
                    ? rotuloApagaEm(c.contagem_inicio_em).toLowerCase()
                    : "fica até alguém apagar"}
              </>
            )}
          </p>

          {c.detalhesVisiveis && c.autorNome && (
            <p className="text-xs text-muted">
              Publicado por {c.autorNome}. Só líderes e gestores do elo veem isto.
            </p>
          )}

          {soPara && (
            <p className="rounded-lg bg-primary/10 px-3 py-2 text-xs text-text">
              Este comunicado é só para {soPara}. Você o vê porque é líder ou gestor do elo. Só esses
              ministérios e a gestão do elo veem.
            </p>
          )}

          <p className="whitespace-pre-line text-[15px] leading-relaxed text-text">{c.corpo}</p>

          <div className="mt-2 flex gap-2">
            {podeEditar && c.situacao === "publicado" && (
              <Button
                type="button"
                onClick={() => onEditar(c)}
                className="flex-1 border border-border bg-transparent text-text hover:bg-surface-2"
              >
                Editar
              </Button>
            )}
            <Button type="button" onClick={onFechar} className="flex-1">
              Fechar
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
